import { memo, useCallback, useMemo } from 'react';

import type { Upload } from '@didaskill/domain/uploads/types';

import { FileBox } from '../../../data-display/file-box/FileBox';
import { useDownloadingState } from '../../../data-display/file-download/useDownloadingState';
import type { FileWithId } from '../types';

export type FileUploadedProps = {
  /** File data. */
  file: Upload | FileWithId;
  /** Use the file as the form value instead of id. */
  useFileAsValue?: boolean;
  /** Call with the file id to remove it from the form. */
  onRemove?: (fileIdOrFile: string | File) => void;
  /** Call with the file id to download it. */
  onDownload?: () => void;
  /** Is the remove button disabled. */
  disabled?: boolean;
};

export const FileUploadedBase = ({
  file,
  useFileAsValue = false,
  disabled = false,
  onRemove,
  onDownload,
}: FileUploadedProps) => {
  const onRemoveProxy = useCallback(() => {
    onRemove(file.id);
  }, [onRemove, file]);

  const fileProperties = useMemo(() => {
    return {
      fileName: useFileAsValue ? (file as FileWithId)?.file?.name : (file as Upload).fileName,
      mimeType: useFileAsValue ? (file as FileWithId)?.file?.type : (file as Upload).mimeType,
    };
  }, [file, useFileAsValue]);

  const { isDownloading, onDownloadProxy } = useDownloadingState(onDownload, disabled);

  return (
    <FileBox
      fileName={fileProperties.fileName}
      mimeType={fileProperties.mimeType}
      isDisabled={disabled}
      isDownloading={isDownloading}
      onRemove={onRemoveProxy}
      onDownload={onDownload ? onDownloadProxy : undefined}
    />
  );
};

FileUploadedBase.displayName = 'FileUploaded';

export const FileUploaded = memo(FileUploadedBase);
